import { useEffect, useState, useRef } from "react";
import { MessageType, type ServerMessage } from "protocol";
import { connect, disconnect } from "../services/websocket-client";
import { startPinging, stopPinging } from "../services/ping-service";

const WS_URL = process.env.BUN_PUBLIC_WS_URL || "ws://localhost:3001/ws";

interface WebSocketState {
  logs: string[];
  engineOn: boolean;
  pitchPosition: number;
  playerColor: string | null;
}

interface WebSocketActions {
  addLog: (message: string) => void;
  setEngineOn: (on: boolean) => void;
  setPitchPosition: (position: number) => void;
}

export function useWebSocket(): WebSocketState & WebSocketActions {
  const [logs, setLogs] = useState<string[]>([]);
  const [engineOn, setEngineOn] = useState(false);
  const [pitchPosition, setPitchPosition] = useState(0);
  const [playerColor, setPlayerColor] = useState<string | null>(null);
  const userIdRef = useRef<string | null>(null);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
  };

  useEffect(() => {
    const handleMessage = (message: ServerMessage): void => {
      switch (message.type) {
        case MessageType.Pong:
          addLog("Received pong from server");
          break;
        case MessageType.UserConnected:
          userIdRef.current = message.userId;
          if (message.color) {
            setPlayerColor(message.color);
          }
          console.log(`User connected: ${message.userId} (${message.color || "no color"})`);
          addLog(`User connected: ${message.userId} (${message.color || "no color"})`);
          break;
        case MessageType.PlayerDeath:
          if (message.playerId === userIdRef.current) {
            setEngineOn(false);
            setPitchPosition(0);
            addLog(`You crashed into ${message.cause}!`);
          } else {
            addLog(`Player ${message.playerId} died by hitting ${message.cause}`);
          }
          break;
        case MessageType.PlayerRespawn:
          if (message.playerId === userIdRef.current) {
            setEngineOn(message.engineOn);
            setPitchPosition(0);
            addLog(`You respawned (engine: ${message.engineOn ? "ON" : "OFF"})`);
          } else {
            addLog(
              `Player ${message.playerId} respawned (engine: ${message.engineOn ? "ON" : "OFF"})`,
            );
          }
          break;
        default:
          addLog(`Unknown message type: ${(message as { type: string }).type}`);
      }
    };

    connect(WS_URL, {
      onOpen: () => {
        addLog("WebSocket connected");
        startPinging(() => {
          addLog("Sent ping to server");
        });
      },
      onClose: () => {
        addLog("WebSocket disconnected");
        stopPinging();
      },
      onError: () => {
        addLog("WebSocket error occurred");
      },
      onMessage: handleMessage,
    });

    return () => {
      stopPinging();
      disconnect();
    };
  }, []);

  return {
    logs,
    engineOn,
    pitchPosition,
    playerColor,
    addLog,
    setEngineOn,
    setPitchPosition,
  };
}
